import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Globe } from 'lucide-react';
import { useLocale, LOCALES } from '../contexts/LocaleContext';

interface LanguageSwitcherProps {
  isMobile?: boolean;
  minimal?: boolean;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ isMobile = false, minimal = false }) => {
  const { currentLocale, setLocale } = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleLocaleChange = (localeCode: string) => {
    setLocale(localeCode);
    setIsOpen(false);
  };

  // Minimal version - simple text with dropdown
  if (minimal) {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-center px-2 py-1 rounded-md hover:bg-gray-100 transition-colors text-sm text-gray-600 hover:text-gray-900"
          aria-label="Change language"
          title={currentLocale.name}
        >
          {currentLocale.code.toUpperCase()}
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-40 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
            {Object.values(LOCALES).map((locale) => (
              <button
                key={locale.code}
                onClick={() => handleLocaleChange(locale.code)}
                className={`w-full flex items-center px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                  currentLocale.code === locale.code ? 'bg-blue-50' : ''
                }`}
              >
                <span className={`text-sm ${currentLocale.code === locale.code ? 'text-blue-600 font-medium' : 'text-gray-700'}`}>
                  {locale.name}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Mobile version - full width button
  if (isMobile) {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium"
        >
          <div className="flex items-center">
            <Globe className="w-5 h-5 mr-3" />
            <span>{currentLocale.name}</span>
          </div>
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
            {Object.values(LOCALES).map((locale) => (
              <button
                key={locale.code}
                onClick={() => handleLocaleChange(locale.code)}
                className={`w-full flex items-center px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${
                  currentLocale.code === locale.code ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                }`}
              >
                <div className="flex-1 text-left">
                  <span className="font-medium">{locale.name}</span>
                </div>
                {currentLocale.code === locale.code && (
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Desktop version - compact button
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
        aria-label="Change language"
      >
        <span className="text-sm text-gray-700">{currentLocale.name}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          {Object.values(LOCALES).map((locale) => (
            <button
              key={locale.code}
              onClick={() => handleLocaleChange(locale.code)}
              className={`w-full flex items-center px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                currentLocale.code === locale.code ? 'bg-blue-50' : ''
              }`}
            >
              <span className={`flex-1 text-left ${currentLocale.code === locale.code ? 'text-blue-600 font-medium' : 'text-gray-700'}`}>
                {locale.name}
              </span>
              {currentLocale.code === locale.code && (
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher; 